import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { AnalyticsStep } from '@atlaskit/adf-schema/steps';
import { FabricChannel } from '@atlaskit/analytics-listeners/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { getPackageInfo } from '../utils/version';
import { EDITOR_AI_PLATFORM_FEATURE_FLAGS } from './platform-feature-flags';
export function getAIFeatureFlags() {
  var editorAiFeatureFlags = {};

  /**
   * Note that we are not required to cleanup fg usage here.
   * This is a helper function to get active Editor AI feature flags.
   *
   * We also trim flag keys in our events as a temporary measure against
   * Statsig errors when metadata is too large.
   */
  Object.keys(EDITOR_AI_PLATFORM_FEATURE_FLAGS).forEach(function (flagKey) {
    var EDITOR_FLAG_PREFIX = 'platform.editor.';
    var trimmedFlagKey = flagKey.startsWith(EDITOR_FLAG_PREFIX) ? flagKey.replace(EDITOR_FLAG_PREFIX, '') : flagKey;
    // eslint-disable-next-line @atlaskit/platform/static-feature-flags
    editorAiFeatureFlags[trimmedFlagKey] = fg(flagKey);
  });
  return editorAiFeatureFlags;
}
export function addAIPluginCommonInfoToPayload(payload) {
  var packageInfo = getPackageInfo();
  var attributes = payload.attributes || {};
  return _objectSpread(_objectSpread({}, payload), {}, {
    attributes: _objectSpread(_objectSpread({}, attributes), {}, {
      aiPluginPackageName: packageInfo.packageName,
      aiPluginPackageVersion: packageInfo.packageVersion
    })
  });
}

// This utils is referenced from packages/editor/editor-core/src/analytics-api/attach-payload-into-transaction.ts
// Note: this adds extra step to a transaction which effectively marks it as docChanged and can cause editor onchange to fire on dispatch
function attachPayloadIntoTransaction(_ref) {
  var editorState = _ref.editorState,
    payload = _ref.payload,
    _ref$channel = _ref.channel,
    channel = _ref$channel === void 0 ? FabricChannel.editor : _ref$channel;
  return function (tr) {
    var storedMarks = tr.storedMarks;
    var pos = tr.mapping.map(editorState.selection.$from.pos, -1);
    tr.step(new AnalyticsStep([{
      payload: addAIPluginCommonInfoToPayload(payload),
      channel: channel
    }], [],
    // We don't understand now why we need position in AnalyticsStep.
    // So we are keeping same as it is in original util.
    // Below comment is taken from original util from packages/editor/editor-core/src/analytics-api/attach-payload-into-transaction.ts
    // We need to create the step based on a position, this prevent split history for relative changes.
    pos));

    // We don't understand now why we need to store marks again in transaction.
    // So we are keeping same as it is in original util.
    // Below comment is taken from original util from packages/editor/editor-core/src/analytics-api/attach-payload-into-transaction.ts
    // When you add a new step all the storedMarks are removed it
    if (storedMarks) {
      tr.setStoredMarks(storedMarks);
    }
    return true;
  };
}

/**
 *  This will add extra step to the transaction with analytics info
 *  this information will be be picked up by the analytics plugin which will fire the event
 *  please note that this will cause the transaction to be marked as docChanged because of adding extra transaction step
 *  avoid creating and dispatching adhoc transaction, but rather reuse existing transactions that are already dispatched
 *
 * The `addToHistory` parameter controls whether this transaction should be recorded in the
 * editor's history. By default, `addToHistory` is set to `true`, meaning the transaction
 * will be added to the history stack, allowing for undo/redo operations. However, if
 * `addToHistory` is set to `false`, the transaction will not be recorded in the history,
 * which can be useful for operations that should not be undoable, such as logging or
 * non-destructive analytics events.
 */
export function addAnalytics(_ref2) {
  var editorState = _ref2.editorState,
    tr = _ref2.tr,
    payload = _ref2.payload,
    _ref2$channel = _ref2.channel,
    channel = _ref2$channel === void 0 ? FabricChannel.editor : _ref2$channel,
    _ref2$addToHistory = _ref2.addToHistory,
    addToHistory = _ref2$addToHistory === void 0 ? true : _ref2$addToHistory;
  attachPayloadIntoTransaction({
    editorState: editorState,
    payload: payload,
    channel: channel
  })(tr);
  if (!addToHistory) {
    tr.setMeta('addToHistory', false);
  }
  return tr;
}

// File copied from packages/editor/editor-core/src/types/command.ts

// Below function is copied from packages/editor/editor-core/src/plugins/analytics/utils.ts
export function withAnalytics(_ref3) {
  var payload = _ref3.payload,
    _ref3$channel = _ref3.channel,
    channel = _ref3$channel === void 0 ? FabricChannel.editor : _ref3$channel;
  return function (command) {
    return function (state, dispatch, view) {
      return command(state, function (tr) {
        if (dispatch) {
          if (payload instanceof Function) {
            var dynamicPayload = payload(state);
            if (dynamicPayload) {
              dispatch(addAnalytics({
                editorState: state,
                tr: tr,
                payload: dynamicPayload,
                channel: channel
              }));
            }
          } else {
            dispatch(addAnalytics({
              editorState: state,
              tr: tr,
              payload: payload,
              channel: channel
            }));
          }
        }
      }, view);
    };
  };
}
export function createFireAIAnalyticsEvent(createAnalyticsEvent) {
  return function (_ref4) {
    var payload = _ref4.payload,
      _ref4$channel = _ref4.channel,
      channel = _ref4$channel === void 0 ? FabricChannel.editor : _ref4$channel;
    if (!createAnalyticsEvent) {
      return;
    }
    var fireEvent = function fireEvent() {
      var _createAnalyticsEvent;
      return (_createAnalyticsEvent = createAnalyticsEvent(addAIPluginCommonInfoToPayload(payload))) === null || _createAnalyticsEvent === void 0 ? void 0 : _createAnalyticsEvent.fire(channel);
    };
    if (window.requestIdleCallback) {
      window.requestIdleCallback(fireEvent);
    } else if (window.requestAnimationFrame) {
      window.requestAnimationFrame(fireEvent);
    } else {
      setTimeout(fireEvent, 0);
    }
  };
}

/*
 * gets helper function to fire analytics for ai plugin
 */
export function useFireAIAnalyticsEvent() {
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var fireAIAnalyticsEvent = createFireAIAnalyticsEvent(createAnalyticsEvent);
  return fireAIAnalyticsEvent;
}

/**
 * Gets the single instrumentation ID from the given channel ID and last AI interaction ID.
 */
export function getSingleInstrumentationID(channelId, lastAiInteractionID) {
  var _ref5;
  return (_ref5 = channelId !== null && channelId !== void 0 ? channelId : lastAiInteractionID) !== null && _ref5 !== void 0 ? _ref5 : '';
}

/**
 * Checks if the given step is an error step.
 */
export function isErrorStepAttributes(step) {
  if (!step) {
    return false;
  }
  return 'errorKey' in step || 'errorType' in step || 'statusCode' in step;
}